import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Hub as HubIcon,
  RotateLeft as RotateLeftIcon,
} from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import useSWR, { mutate as globalMutate } from 'swr';
import { request } from '@/api/client';
import { DataTable } from '@/components/DataTable';
import { StatusPill } from '@/components/StatusPill';
import { useToast } from '@/components/Toaster';
import { PageShell } from '@/layout/PageShell';
import { utcStamp } from '@/utils/format';

/**
 * L1 ABDM Bridge admin (FRD §8.0.10). Exactly one row per environment;
 * the seeder pre-creates a SANDBOX placeholder. Operators paste the
 * ABDM-issued client_id / client_secret here. These credentials are NOT
 * exposed to any consumer org or tenant — they authenticate the platform
 * itself to the ABDM gateway.
 */

interface PlatformBridgeRow {
  id: string;
  bridgeId: string;
  displayName: string;
  environment: 'SANDBOX' | 'PRODUCTION' | string;
  abdmClientId?: string;
  /** Backend never returns the secret on read — only a flag indicating it's set. */
  abdmClientSecretSet: boolean;
  gatewayUrl?: string;
  status: 'ACTIVE' | 'SUSPENDED' | string;
  rotatedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

const SWR_KEY = '/admin/platform-bridges';

export function PlatformBridgePage() {
  const theme = useTheme();
  const [editing, setEditing] = useState<PlatformBridgeRow | null>(null);

  const { data, error, isLoading } = useSWR<PlatformBridgeRow[]>(
    SWR_KEY,
    () => request<PlatformBridgeRow[]>({ url: SWR_KEY, method: 'GET' }),
  );
  const refresh = () => globalMutate(SWR_KEY);

  return (
    <PageShell
      kicker="PLATFORM · BRIDGE"
      title="ABDM Bridge"
      subtitle="L1 credentials the platform uses to authenticate to the ABDM gateway. One row per environment. Never exposed to consumer orgs or tenants."
      width="wide"
    >
      <Alert severity="info" variant="outlined" sx={{ mb: 2, borderRadius: theme.radius.sm + 'px' }}>
        Per FRD §8.0, ABDM-issued <code>client_id</code> / <code>client_secret</code> live ONLY here.
        Tenants identify themselves to ABDM via <code>HIP-ID</code>/<code>HIU-ID</code> in request bodies — never via this credential.
      </Alert>

      <DataTable<PlatformBridgeRow>
        rows={data}
        rowKey={(r) => r.id}
        isLoading={isLoading}
        error={error ? { message: (error as Error).message } : null}
        empty={{
          icon: HubIcon,
          title: 'No bridge configured.',
          description: 'The seeder normally creates a SANDBOX placeholder on boot — restart the connector if this is empty.',
        }}
        columns={[
          {
            key: 'env',
            header: 'Environment',
            cell: (r) => (
              <Stack spacing={0}>
                <Typography variant="body2" fontWeight={500}>{r.environment}</Typography>
                <Typography variant="caption" color="text.tertiary">{r.displayName}</Typography>
              </Stack>
            ),
            width: 200,
            pinned: true,
          },
          {
            key: 'gw',
            header: 'Gateway',
            cell: (r) => (
              <Typography variant="caption" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {r.gatewayUrl ?? '—'}
              </Typography>
            ),
          },
          {
            key: 'cid',
            header: 'Client ID',
            cell: (r) => (
              <Typography variant="caption" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {r.abdmClientId ?? <em>—</em>}
              </Typography>
            ),
            width: 200,
          },
          {
            key: 'secret',
            header: 'Secret',
            cell: (r) => (r.abdmClientSecretSet
              ? <StatusPill tone="success">Set</StatusPill>
              : <StatusPill tone="warning">Not set</StatusPill>),
            width: 110,
          },
          {
            key: 'status',
            header: 'Status',
            cell: (r) => (r.status === 'ACTIVE'
              ? <StatusPill tone="success">{r.status}</StatusPill>
              : <StatusPill tone="mono">{r.status}</StatusPill>),
            width: 110,
          },
          {
            key: 'rot',
            header: 'Rotated',
            cell: (r) => (
              <Typography variant="caption" color="text.secondary">
                {r.rotatedAt ? utcStamp(r.rotatedAt) : '—'}
              </Typography>
            ),
            width: 180,
          },
          {
            key: 'actions',
            header: '',
            align: 'right',
            width: 110,
            pinned: true,
            cell: (r) => (
              <Button
                size="small"
                startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                onClick={() => setEditing(r)}
              >
                Edit
              </Button>
            ),
          },
        ]}
      />

      <BridgeEditDialog
        bridge={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); refresh(); }}
      />
    </PageShell>
  );
}

interface BridgeForm {
  displayName: string;
  abdmClientId: string;
  abdmClientSecret: string;
  gatewayUrl: string;
  status: 'ACTIVE' | 'SUSPENDED';
}

function BridgeEditDialog({
  bridge,
  onClose,
  onSaved,
}: {
  bridge: PlatformBridgeRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const theme = useTheme();
  const toast = useToast();
  const [rotating, setRotating] = useState(false);

  const { control, handleSubmit, reset, formState: { isSubmitting } } = useForm<BridgeForm>({
    defaultValues: {
      displayName: '',
      abdmClientId: '',
      abdmClientSecret: '',
      gatewayUrl: '',
      status: 'ACTIVE',
    },
  });

  useEffect(() => {
    if (bridge) {
      reset({
        displayName: bridge.displayName ?? '',
        abdmClientId: bridge.abdmClientId ?? '',
        abdmClientSecret: '',  // write-only, never pre-filled
        gatewayUrl: bridge.gatewayUrl ?? '',
        status: bridge.status === 'SUSPENDED' ? 'SUSPENDED' : 'ACTIVE',
      });
    }
  }, [bridge, reset]);

  const onSubmit = async (form: BridgeForm) => {
    if (!bridge) return;
    try {
      await request({
        url: `/admin/platform-bridges/${encodeURIComponent(bridge.id)}`,
        method: 'PATCH',
        data: {
          displayName: form.displayName.trim(),
          abdmClientId: form.abdmClientId.trim() || undefined,
          ...(form.abdmClientSecret ? { abdmClientSecret: form.abdmClientSecret } : {}),
          gatewayUrl: form.gatewayUrl.trim() || undefined,
          status: form.status,
        },
      });
      toast.success('Bridge updated', `${bridge.environment} · ${form.displayName}`);
      onSaved();
    } catch (e) {
      const detail = (e as { response?: { data?: { error?: string; message?: string } } })?.response?.data;
      toast.error('Save failed', detail?.message ?? detail?.error ?? (e as Error).message);
    }
  };

  const onRotateOnly = handleSubmit(async (form) => {
    if (!bridge || !form.abdmClientSecret) {
      toast.error('Paste the new secret first');
      return;
    }
    setRotating(true);
    try {
      const r = await request<{ rotatedAt: string }>({
        url: `/admin/platform-bridges/${encodeURIComponent(bridge.id)}/rotate-secret`,
        method: 'POST',
        data: { abdmClientSecret: form.abdmClientSecret },
      });
      toast.success('Secret rotated', `at ${r.rotatedAt}`);
      onSaved();
    } catch (e) {
      const detail = (e as { response?: { data?: { error?: string; message?: string } } })?.response?.data;
      toast.error('Rotate failed', detail?.message ?? detail?.error ?? (e as Error).message);
    } finally {
      setRotating(false);
    }
  });

  return (
    <Dialog open={!!bridge} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: theme.radius.lg + 'px' } }}>
      <DialogTitle sx={{ pr: 6, fontWeight: 600 }}>
        {bridge?.environment} bridge
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Box component="form" id="bridge-form" onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Controller
              name="displayName"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="Display name" fullWidth />
              )}
            />
            <Controller
              name="gatewayUrl"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Gateway URL"
                  fullWidth
                  helperText="dev.abdm.gov.in/gateway for sandbox; healthidsbx.abdm.gov.in (or current prod) for production."
                />
              )}
            />
            <Controller
              name="abdmClientId"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="ABDM Client ID" fullWidth helperText="Issued by NHA on bridge registration." />
              )}
            />
            <Controller
              name="abdmClientSecret"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="password"
                  label="ABDM Client Secret"
                  fullWidth
                  autoComplete="new-password"
                  helperText={bridge?.abdmClientSecretSet
                    ? 'Currently set. Paste a new value only when rotating.'
                    : 'Required — paste the secret NHA issued.'}
                />
              )}
            />
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="Status" select fullWidth>
                  <MenuItem value="ACTIVE">Active</MenuItem>
                  <MenuItem value="SUSPENDED">Suspended (stops every tenant in this env)</MenuItem>
                </TextField>
              )}
            />
            <Alert severity="warning" variant="outlined" sx={{ borderRadius: theme.radius.sm + 'px' }}>
              Suspending a bridge halts ABDM calls for every tenant in this environment. Use the dedicated rotate-secret button below if you only need to rotate.
            </Alert>
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={() => onRotateOnly()}
          disabled={rotating || isSubmitting}
          startIcon={<RotateLeftIcon fontSize="small" />}
        >
          {rotating ? 'Rotating…' : 'Rotate secret only'}
        </Button>
        <Button
          type="submit"
          form="bridge-form"
          variant="contained"
          disabled={isSubmitting || rotating}
          sx={{ bgcolor: theme.palette.brand.main, '&:hover': { bgcolor: theme.palette.brand.dark } }}
        >
          {isSubmitting ? 'Saving…' : 'Save changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
